import { create } from 'zustand';
import type { AccountProfile } from '../domain/types';
import { ApiError, apiFetch, authClient, authConfigured } from '../lib/api';
import { isAdminPortal } from '../lib/portal';

type AuthMode = 'loading' | 'local' | 'anonymous' | 'authenticated';
type AuthState = {
  mode: AuthMode;
  initialized: boolean;
  user: AccountProfile | null;
  profile: AccountProfile | null;
  error: string | null;
  initialize: () => Promise<void>;
  signInWithProvider: (provider: 'github') => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  clearError: () => void;
};

let initializePromise: Promise<void> | null = null;
const PROFILE_CACHE_KEY = 'sts2stats.cachedProfile';

function readCachedProfile(): AccountProfile | null {
  try {
    const value = JSON.parse(localStorage.getItem(PROFILE_CACHE_KEY) || 'null') as Partial<AccountProfile> | null;
    return value && typeof value.id === 'string' && typeof value.email === 'string' && value.status === 'active' ? value as AccountProfile : null;
  } catch { return null; }
}

function cacheProfile(profile: AccountProfile | null) {
  try {
    if (profile) localStorage.setItem(PROFILE_CACHE_KEY, JSON.stringify(profile));
    else localStorage.removeItem(PROFILE_CACHE_KEY);
  } catch { /* offline profile caching is best-effort */ }
}

async function fetchProfile(): Promise<AccountProfile> {
  return (await apiFetch<{ profile: AccountProfile }>('/api/me')).profile;
}

export const useAuthStore = create<AuthState>((set, get) => ({
  mode: 'loading', initialized: false, user: null, profile: null, error: null,
  async initialize() {
    if (get().initialized) return;
    if (initializePromise) return initializePromise;
    initializePromise = (async () => {
      if (!authConfigured) { set({ mode: 'local', initialized: true }); return; }
      try {
        const profile = await fetchProfile();
        cacheProfile(profile);
        set({ mode: 'authenticated', initialized: true, user: profile, profile, error: null });
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) {
          cacheProfile(null);
          set({ mode: 'anonymous', initialized: true, user: null, profile: null, error: null });
          return;
        }
        const cached = !isAdminPortal && (!(error instanceof ApiError) || !navigator.onLine) ? readCachedProfile() : null;
        if (cached) {
          set({ mode: 'authenticated', initialized: true, user: cached, profile: cached, error: '当前处于离线模式，只显示本账号的本地存档副本。' });
          return;
        }
        set({ mode: 'anonymous', initialized: true, user: null, profile: null, error: error instanceof Error ? error.message : String(error) });
      }
    })();
    try { await initializePromise; } finally { initializePromise = null; }
  },
  async signInWithProvider(provider) {
    set({ error: null });
    const result = await authClient.signIn.social({ provider, callbackURL: window.location.href });
    if (result.error) {
      const error = new Error(result.error.message || '无法开始第三方登录。');
      set({ error: error.message });
      throw error;
    }
  },
  async signOut() {
    const result = await authClient.signOut();
    if (result.error) {
      const error = new Error(result.error.message || '退出失败。');
      set({ error: error.message });
      throw error;
    }
    set({ mode: 'anonymous', user: null, profile: null });
    cacheProfile(null);
  },
  async refreshProfile() {
    try {
      const profile = await fetchProfile();
      cacheProfile(profile);
      set({ mode: 'authenticated', user: profile, profile, error: null });
    } catch (error) {
      set({ error: error instanceof Error ? error.message : String(error) });
    }
  },
  clearError: () => set({ error: null })
}));
